import { useContext } from "react";
import { Box, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { RootCauseAnalysisData } from "../../types";

export type AnaliseCausaRaizChartProps = {
    data: RootCauseAnalysisData;
};

export default function AnaliseCausaRaizChart({ data }: AnaliseCausaRaizChartProps) {
    const { theme } = useContext(MainContext);

    const recoverableLosses = data.recoverableLossesTotal;
    const expectedLosses = data.expectedLossesTotal;
    const totalLosses = recoverableLosses + expectedLosses;

    const avoidablePercent = totalLosses > 0 ? (recoverableLosses / totalLosses) * 100 : 0;
    const unavoidablePercent = totalLosses > 0 ? (expectedLosses / totalLosses) * 100 : 0;

    const chartOption: EChartsOption = {
        backgroundColor: "transparent",
        tooltip: {
            show: false
        },
        grid: {
            left: "3%",
            right: "3%",
            bottom: "3%",
            top: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "value",
            show: false,
            max: 100
        },
        yAxis: {
            type: "category",
            data: [""],
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false }
        },
        series: [
            {
                name: "Gerenciáveis",
                type: "bar",
                stack: "total",
                label: {
                    show: true,
                    formatter: `${avoidablePercent.toFixed(2)}%`,
                    position: "inside",
                    color: "#FFFFFF",
                    fontSize: 14,
                    fontWeight: "bold"
                },
                data: [avoidablePercent],
                itemStyle: {
                    color: "#f59e0b",
                    borderRadius: [8, 0, 0, 8]
                },
                barWidth: "35%"
            },
            {
                name: "Previstas",
                type: "bar",
                stack: "total",
                label: {
                    show: true,
                    formatter: `${unavoidablePercent.toFixed(2)}%`,
                    position: "inside",
                    color: "#FFFFFF",
                    fontSize: 14,
                    fontWeight: "bold"
                },
                data: [unavoidablePercent],
                itemStyle: {
                    color: "#60A5FA",
                    borderRadius: [0, 8, 8, 0]
                }
            }
        ]
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                padding: "16px",
                height: 350,
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", paddingX: "25px" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px" }}>
                    Proporção de Perdas
                </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {totalLosses === 0 ? (
                    <Typography sx={{ color: theme.secondaryTextColor, fontSize: "14px", fontStyle: "italic" }}>
                        Nenhuma perda registrada no período
                    </Typography>
                ) : (
                    <RopChart option={chartOption} />
                )}
            </Box>

            {/* Legend */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: "24px", marginTop: "16px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Box
                        sx={{
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#f59e0b",
                            borderRadius: "2px"
                        }}
                    />
                    <Typography sx={{ fontSize: "14px" }}>
                        Gerenciáveis ({recoverableLosses.toFixed(2)} MWh)
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Box
                        sx={{
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#60A5FA",
                            borderRadius: "2px"
                        }}
                    />
                    <Typography sx={{ fontSize: "14px" }}>
                        Previstas ({expectedLosses.toFixed(2)} MWh)
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
